import React, { useState, useEffect } from "react";
import axios from "axios";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { JOB_AREAS } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SPONTANEA = "Candidatura spontanea";
const MAX_CV_BYTES = 5 * 1024 * 1024; // 5MB

const schema = z.object({
  nome: z.string().min(2, "Inserisci nome e cognome"),
  email: z.string().email("Email non valida"),
  telefono: z.string().min(5, "Inserisci un telefono valido"),
  area: z.string().optional().or(z.literal("")),
  posizione: z.string().optional().or(z.literal("")),
  link: z.string().url("URL non valido").optional().or(z.literal("")),
  messaggio: z.string().optional().or(z.literal("")),
  privacy: z.literal(true, { errorMap: () => ({ message: "Accetta la privacy policy per inviare" }) }),
});

const ApplicationForm = ({ prefillPosition = "", onSubmitted }) => {
  const [submitting, setSubmitting] = useState(false);
  const [cvFile, setCvFile] = useState(null);
  const [cvError, setCvError] = useState("");
  const isSpontanea = prefillPosition === SPONTANEA || !prefillPosition;

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { posizione: prefillPosition, privacy: false },
  });

  useEffect(() => {
    setValue("posizione", prefillPosition || "");
  }, [prefillPosition, setValue]);

  const handleCvChange = (e) => {
    setCvError("");
    const file = e.target.files?.[0];
    if (!file) {
      setCvFile(null);
      return;
    }
    if (file.type !== "application/pdf") {
      setCvError("Il CV deve essere in formato PDF.");
      setCvFile(null);
      e.target.value = "";
      return;
    }
    if (file.size > MAX_CV_BYTES) {
      setCvError("Il CV non può superare i 5 MB.");
      setCvFile(null);
      e.target.value = "";
      return;
    }
    setCvFile(file);
  };

  const onSubmit = async (values) => {
    // CV obbligatorio
    if (!cvFile) {
      setCvError("Il CV in PDF è obbligatorio.");
      return;
    }
    setSubmitting(true);
    try {
      // multipart/form-data: NON impostare Content-Type — axios lo gestisce da solo con il boundary
      const form = new FormData();
      form.append("nome", values.nome);
      form.append("email", values.email);
      form.append("telefono", values.telefono || "");
      form.append("area", isSpontanea ? (values.area || "") : "");
      form.append("posizione", values.posizione || SPONTANEA);
      form.append("link", values.link || "");
      form.append("messaggio", values.messaggio || "");
      form.append("gdpr_consent", "true");
      form.append("honeypot", "");
      form.append("cv", cvFile);

      await axios.post(`${API}/applications`, form);

      toast.success("Candidatura ricevuta. Ti contatteremo presto.");
      reset();
      setCvFile(null);
      setCvError("");
      if (onSubmitted) onSubmitted();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail || "Qualcosa è andato storto. Riprova o scrivici a hello@weareonsports.com");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="grid grid-cols-1 md:grid-cols-2 gap-5 text-dodgers"
      noValidate
      data-testid="application-form"
    >
      <div>
        <label className="font-condensed uppercase tracking-widest text-xs opacity-70 block mb-2">
          Nome e cognome *
        </label>
        <input className="input-os" {...register("nome")} data-testid="app-nome" />
        {errors.nome && <p className="text-rosso-enzo text-sm mt-1">{errors.nome.message}</p>}
      </div>
      <div>
        <label className="font-condensed uppercase tracking-widest text-xs opacity-70 block mb-2">
          Email *
        </label>
        <input type="email" className="input-os" {...register("email")} data-testid="app-email" />
        {errors.email && <p className="text-rosso-enzo text-sm mt-1">{errors.email.message}</p>}
      </div>
      <div>
        <label className="font-condensed uppercase tracking-widest text-xs opacity-70 block mb-2">
          Telefono *
        </label>
        <input className="input-os" {...register("telefono")} data-testid="app-telefono" />
        {errors.telefono && <p className="text-rosso-enzo text-sm mt-1">{errors.telefono.message}</p>}
      </div>
      <div>
        <label className="font-condensed uppercase tracking-widest text-xs opacity-70 block mb-2">
          Posizione
        </label>
        <input
          className="input-os"
          placeholder="Es. Business Developer"
          readOnly={!!prefillPosition && prefillPosition !== SPONTANEA}
          {...register("posizione")}
          data-testid="app-posizione"
        />
      </div>

      {isSpontanea && (
        <div className="md:col-span-2">
          <label className="font-condensed uppercase tracking-widest text-xs opacity-70 block mb-2">
            Area di interesse
          </label>
          <select className="input-os" {...register("area")} data-testid="app-area">
            <option value="">Seleziona…</option>
            {JOB_AREAS.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="md:col-span-2">
        <label className="font-condensed uppercase tracking-widest text-xs opacity-70 block mb-2">
          Link LinkedIn / Portfolio
        </label>
        <input
          className="input-os"
          placeholder="https://..."
          {...register("link")}
          data-testid="app-link"
        />
        {errors.link && <p className="text-rosso-enzo text-sm mt-1">{errors.link.message}</p>}
      </div>

      <div className="md:col-span-2">
        <label className="font-condensed uppercase tracking-widest text-xs opacity-70 block mb-2">
          CV (PDF · max 5MB) *
        </label>
        <input
          type="file"
          accept="application/pdf,.pdf"
          onChange={handleCvChange}
          required
          className="input-os file:mr-4 file:py-1 file:px-3 file:border file:border-dodgers file:bg-transparent file:text-dodgers file:font-condensed file:uppercase file:tracking-widest file:text-[12px] cursor-pointer"
          data-testid="app-cv"
        />
        {cvFile && (
          <p className="text-dodgers/70 text-sm mt-1" data-testid="app-cv-info">
            ✓ {cvFile.name} · {(cvFile.size / 1024 / 1024).toFixed(2)} MB
          </p>
        )}
        {cvError && <p className="text-rosso-enzo text-sm mt-1" data-testid="app-cv-error">{cvError}</p>}
      </div>

      <div className="md:col-span-2">
        <label className="font-condensed uppercase tracking-widest text-xs opacity-70 block mb-2">
          Lettera motivazionale / Messaggio
        </label>
        <textarea
          className="input-os min-h-[140px]"
          {...register("messaggio")}
          data-testid="app-messaggio"
        />
      </div>

      <div className="md:col-span-2 flex items-start gap-3 text-sm leading-snug">
        <input
          type="checkbox"
          id="app-privacy"
          {...register("privacy")}
          className="mt-1 w-4 h-4 accent-lime"
          data-testid="app-privacy"
        />
        <label htmlFor="app-privacy" className="opacity-90">
          Ho letto e accetto la{" "}
          <Link to="/privacy-policy" className="underline" target="_blank" rel="noopener noreferrer">
            privacy policy
          </Link>{" "}
          *
        </label>
      </div>
      {errors.privacy && <p className="text-rosso-enzo text-sm md:col-span-2 -mt-2">{errors.privacy.message}</p>}

      <div className="md:col-span-2 pt-3">
        <button
          type="submit"
          disabled={submitting}
          className="btn-os btn-primary"
          data-testid="application-submit"
        >
          <span>{submitting ? "Invio…" : "Invia candidatura"}</span>
          <span className="arrow" aria-hidden="true">→</span>
        </button>
      </div>
    </form>
  );
};

export default ApplicationForm;
